import { Modal, Form, Typography, Button, Switch, Row, Col, DatePicker } from "antd";
import { ModalProps } from "antd";
import { ClearOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import duration from "dayjs/plugin/duration";
import useProductOpenApiClient, { ProductExtraFieldsUpdateRequest } from "apiClients/useProductOpenApiClient";
import { useNotification, useOne, HttpError } from "@refinedev/core";
import { useState, useEffect } from "react";
import LocalisedFieldInput from "components/inputs/LocalisedFieldInput";
import { IProduct } from "interfaces";
import { PRODUCT_RES_ID } from "utility/constants";
import { useLocaleStore } from "stores/localeStore";

dayjs.extend(duration);

const { Title } = Typography;

interface BulkUpdateExtraFieldsModalProps {
  modalProps: ModalProps;
  onFinish: (clearAllKeys: boolean) => void;
  selectedIds: React.Key[];
}

export const BulkUpdateExtraFieldsModal: React.FC<BulkUpdateExtraFieldsModalProps> = ({
  modalProps,
  onFinish,
  selectedIds,
}) => {
  const [form] = Form.useForm();
  const { bulkUpdateProducts, loading: apiLoading, errorMessage } = useProductOpenApiClient();
  const { open } = useNotification();
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const localeState = useLocaleStore();
  
  // Get the first selected product ID to fetch initial values
  const firstProductId = selectedIds.length > 0 ? selectedIds[0] : undefined;
  
  // Fetch the first product to get initial extraFields values
  const { data: firstProductData, isLoading: isLoadingFirstProduct } = useOne<IProduct, HttpError>({
    resource: PRODUCT_RES_ID,
    id: firstProductId as number,
    meta: { headers: { "lang": localeState.locale } },
    queryOptions: {
      enabled: !!firstProductId && !!modalProps.open,
      retry: 1,
    },
  });
  
  // Set initial values when product data is loaded
  useEffect(() => {
    if (firstProductData?.data?.extraFields && modalProps.open) {
      const extraFields = firstProductData.data.extraFields;
      form.setFieldsValue({
        campaignNames: extraFields.campaignNames || [],
        showCampaignCounter: extraFields.showCampaignCounter ?? false,
        modifiedCampaignStartDate: extraFields.modifiedCampaignStartDate 
          ? dayjs(extraFields.modifiedCampaignStartDate) 
          : null,
        modifiedCampaignEndDate: extraFields.modifiedCampaignEndDate 
          ? dayjs(extraFields.modifiedCampaignEndDate) 
          : null,
      });
    } else if (modalProps.open && !isLoadingFirstProduct) {
      // Reset form if no data or when modal opens fresh
      form.resetFields();
    }
  }, [firstProductData, modalProps.open, isLoadingFirstProduct, form]);

  const clearExtraFields = () => {
    form.setFieldsValue({
      campaignNames: [],
      showCampaignCounter: false,
      modifiedCampaignStartDate: null,
      modifiedCampaignEndDate: null,
    });
  };

  const handleFinish = async (values: any) => {
    if (selectedIds.length === 0) {
      onFinish(false);
      return;
    }

    let updateValues: ProductExtraFieldsUpdateRequest = {};

    if (values.campaignNames && values.campaignNames.length > 0) {
      updateValues.campaignNames = values.campaignNames;
    }

    if (values.showCampaignCounter !== undefined) {
      updateValues.showCampaignCounter = values.showCampaignCounter;
    }

    if (values.modifiedCampaignStartDate) {
      if (dayjs.isDayjs(values.modifiedCampaignStartDate)) {
        updateValues.modifiedCampaignStartDate = values.modifiedCampaignStartDate.toISOString();
      } else {
        updateValues.modifiedCampaignStartDate = values.modifiedCampaignStartDate;
      }
    } else if (values.modifiedCampaignStartDate === null) {
      updateValues.modifiedCampaignStartDate = null;
    }

    if (values.modifiedCampaignEndDate) {
      if (dayjs.isDayjs(values.modifiedCampaignEndDate)) {
        updateValues.modifiedCampaignEndDate = values.modifiedCampaignEndDate.toISOString();
      } else {
        updateValues.modifiedCampaignEndDate = values.modifiedCampaignEndDate;
      }
    } else if (values.modifiedCampaignEndDate === null) {
      updateValues.modifiedCampaignEndDate = null;
    }

    if (Object.keys(updateValues).length === 0) {
      onFinish(true);
      return;
    }

    setIsSubmitting(true);
    try {
      const result = await bulkUpdateProducts(
        selectedIds as number[],
        {
          extraFields: updateValues,
        }
      );

      if (errorMessage) {
        open?.({
          message: "Fel vid uppdatering",
          description: errorMessage.message || "Ett fel uppstod vid uppdatering av extra fält",
          type: "error",
        });
      } else if (result) {
        open?.({
          message: "Uppdatering lyckades",
          description: `${selectedIds.length} produkt(er) har uppdaterats`,
          type: "success",
        });
        onFinish(true);
      }
    } catch (e: any) {
      open?.({
        message: "Fel vid uppdatering",
        description: e.message || "Ett fel uppstod vid uppdatering av extra fält",
        type: "error",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Modal
        {...modalProps}
        width={600}
        onCancel={() => {
          form.resetFields();
          onFinish(false);
        }}
        onOk={() => form.submit()}
        confirmLoading={isSubmitting || apiLoading}
        afterClose={() => {
          form.resetFields();
        }}
        footer={(_, { OkBtn, CancelBtn }) => (
          <>
            <Button
              type="default"
              icon={<ClearOutlined />}
              onClick={clearExtraFields}
            >
              Rensa fält
            </Button>
            <CancelBtn />
            <OkBtn />
          </>
        )}
      >
        <Title level={3} style={{ marginBottom: "1rem" }}>
          Uppdatera extra fält
        </Title>
        <p style={{ marginBottom: "1rem", color: "#666" }}>
          Uppdaterar {selectedIds.length} vald(a) produkt(er)
        </p>
        {selectedIds.length > 1 && (
          <p style={{ marginBottom: "1rem", color: "#999", fontSize: "12px", fontStyle: "italic" }}>
            Visar värden från första valda produkten som utgångspunkt
          </p>
        )}
        <Form form={form} layout="vertical" onFinish={handleFinish}>
          <Form.Item
            label="Kampanjnamn"
            name="campaignNames"
            tooltip="Ange kampanjnamn för olika språk. Detta kommer att uppdatera alla valda produkter."
          >
            <LocalisedFieldInput
              placeholder="Ange kampanjnamn"
              type="input"
            />
          </Form.Item>
          <Form.Item
            label="Visa kampanjräknare"
            valuePropName="checked"
            name="showCampaignCounter"
          >
            <Switch size={"default"} />
          </Form.Item>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Modifierat kampanjstartdatum"
                name="modifiedCampaignStartDate"
                getValueProps={(value) => ({
                  value: value ? dayjs(value) : null,
                })}
                normalize={(value) => {
                  return value && dayjs.isDayjs(value) ? value.toISOString() : null;
                }}
              >
                <DatePicker showTime style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Modifierat kampanjslutdatum"
                name="modifiedCampaignEndDate"
                getValueProps={(value) => ({
                  value: value ? dayjs(value) : null,
                })}
                normalize={(value) => {
                  return value && dayjs.isDayjs(value) ? value.toISOString() : null;
                }}
              >
                <DatePicker showTime style={{ width: "100%" }} />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </>
  );
};

